'use client';
import { useNavigate } from 'react-router-dom';
import { useLazyGetQueryRawQuery } from '../state/querySlice';
import { DynamicRequestBody } from '../schemas/DynamicRequestBody';
import { ReturnParameterValue } from '../schemas/ReturnParameterValue';
import { useResourceLibraryItems } from '../graphql/pimcore/modules/resourceLibrary/contexts/ResourceLibraryItemsProvider';
import { useDefinitions } from '../contexts/DefinitionsContext';
import { useEffect, useRef, useState } from 'react';

export const processQueryParameters = (queryParameterValues?: ReturnParameterValue[]) => {
    if (!queryParameterValues) {
        return {};
    }
    return queryParameterValues.reduce((acc: { [key: string]: number[] | boolean }, curr) => {
        // 跳過 parameterId 為 1 跟 79 的情況
        if (curr.parameterId === 1 || curr.parameterId === 79) {
            return acc;
        }

        const key = curr.parameterId.toString();

        if (curr.parameterName.includes('Features') && curr.value === '1') {
            acc[key] = true;
        } else {
            const existing = acc[key];
            if (existing === undefined) {
                acc[key] = [curr.id];
            } else if (Array.isArray(existing)) {
                existing.push(curr.id);
            }
        }
        return acc;
    }, {});
};

export const processQueryParametersTree = (
    queryParameterValues?: ReturnParameterValue[]
): string[] => {
    if (!queryParameterValues?.length) {
        return [];
    }

    const treeValues = queryParameterValues
        .filter((p) => p.parameterId === 1)
        .map((p) => p.formattedValue ?? p.value)
        .filter(Boolean) as string[]; // ⬅ 這裡直接斷言

    if (treeValues.length === 0) {
        return [];
    }
    if (treeValues.length === 1) {
        return [treeValues[0] as string]; // OK：已被推斷為 string
    }
    return treeValues; // string[]
};

function buildSearchQuery(keyword: string, options?: { compare?: string[]; doCompare?: boolean }) {
    const params = new URLSearchParams();
    params.set('search', keyword);

    if (options?.compare && options.compare.length > 0) {
        params.set('compare', options.compare.join(','));
    }

    if (options?.doCompare) {
        params.set('doCompare', 'true');
    }

    return `?${params.toString()}`;
}

interface UseSearchHandlerProps {
    setIsLoading?: (loading: boolean) => void;
    // 可選：若你要這個 Hook 幫你 set loading，就傳入一個 setIsLoading
}

interface PendingSearch {
    keyword: string;
    options?: {
        compare?: string[];
        doCompare?: boolean;
    };
}

/**
 * 把原本在 HeaderSearchBar 的搜尋邏輯抽成 Hook。
 * 它會根據搜尋結果，進行相同的條件判斷並 navigate 到不同頁面。
 */
export function useSearchHandler(props?: UseSearchHandlerProps) {
    const navigate = useNavigate();
    const [getQueryRaw] = useLazyGetQueryRawQuery();

    const {
        items: resourceLibraryItems,
        filteredItems,
        setFilterSync,
        loading: resourceLibraryLoading,
        refetch: refetchResourceLibraryItems,
    } = useResourceLibraryItems();

    const { definitionsMap, rawDefinitions: definitionData } = useDefinitions();

    const [definitionsReady, setDefinitionsReady] = useState(false);
    const pendingSearchRef = useRef<PendingSearch | null>(null);

    // 當 definitionsMap 跟 resourceLibraryItems 準備好，檢查是否有等待中的 keyword
    useEffect(() => {
        if (
            definitionsMap &&
            definitionsMap.size > 0 &&
            !resourceLibraryLoading &&
            resourceLibraryItems.length > 0
        ) {
            setDefinitionsReady(true);
            if (pendingSearchRef.current) {
                const { keyword, options } = pendingSearchRef.current;
                pendingSearchRef.current = null; // 清空
                searchByKeyword(keyword, options); // ⬅ 重試
            }
        }
    }, [definitionsMap, resourceLibraryLoading, resourceLibraryItems]);

    const searchByKeyword = async (
        keyword: string,
        options?: { compare?: string[]; doCompare?: boolean }
    ) => {
        try {
            props?.setIsLoading?.(true);

            const isReady =
                definitionsMap &&
                definitionsMap.size > 0 &&
                !resourceLibraryLoading &&
                resourceLibraryItems.length > 0;

            if (!isReady) {
                console.warn(
                    'definitionsMap or Resource library items not ready yet, deferring search...'
                );
                pendingSearchRef.current = { keyword, options };
                return;
            }

            // 1. 呼叫後端 API，取得結果
            const result = await getQueryRaw({
                input: keyword, // 關鍵字
                targetMfgs: [], // 你可能會帶的額外參數
            }).unwrap();

            // Filter keyword on resource library
            const newFiltered = setFilterSync('searchTerm', keyword); // ✅ 使用 context 過濾關鍵字

            const parameterValues = processQueryParameters(result.queryParameterValues);
            // 2.0 以下示意：根據 result 的內容，決定要 navigate 到哪

            // 放到最前面,優先處理偵測到 uniqueParts的情況
            // https://www.wrike.com/open.htm?id=1660306710
            // single part
            if (result.detectedUniqueParts) {
                if (result.detectedUniqueParts.length === 1) {
                    console.log('result.detectedUniqueParts.length === 1');
                    const currentPart = result.detectedUniqueParts[0];
                    if (currentPart && currentPart.uniquePn) {
                        navigate(
                            `/products/${result.detectedDefinition?.name}/part/${encodeURIComponent(currentPart.uniquePn)}`
                        );
                        return;
                    }
                    // detect multiple unique parts
                } else if (result.detectedUniqueParts.length > 1) {
                    console.log('result.detectedUniqueParts.length > 1');
                    const definition = result.detectedDefinitions[0];

                    // 構建 initialFilterState
                    const initialFilterState: DynamicRequestBody = {
                        '1': definition?.id ? definition?.id : 1, // 基本的 filter state
                        ...parameterValues, // 添加處理後的 parameter values
                    };

                    const singleType =
                        definition?.childDefinitions?.length === 1
                            ? definition.childDefinitions.map((d) => d.id)
                            : undefined;

                    if (singleType?.length) {
                        initialFilterState['79'] = singleType;
                    }

                    navigate(
                        {
                            pathname: `/products/${definition?.name}`,
                            search: buildSearchQuery(keyword, options),
                        },
                        {
                            state: {
                                queryRawResults: result,
                                initialFilterState: initialFilterState,
                                searchInput: keyword,
                                leftOverParam: keyword,
                            },
                        }
                    );
                    return;
                }
            }

            // 2.1 new request https://www.wrike.com/open.htm?id=1653033605
            // if detect query parameterValues contains tree = xxx, go to associate product page

            const tree = processQueryParametersTree(result.queryParameterValues);

            if (tree.length === 1) {
                // ✅ key === '1'，而且只有一個值
                // 在這裡處理「單一 tree 定位到對應產品頁」的導向
                // console.log('tree.length === 1');
                const def = definitionsMap.get(tree[0] as string)?.get(tree[0] as string);
                const initialFilterState: DynamicRequestBody = {
                    '1': def?.id ? def?.id : 1, // 基本的 filter state
                    ...parameterValues, // 添加處理後的 parameter values
                };

                navigate(
                    {
                        pathname: `/products/${def?.name}`,
                        search: buildSearchQuery(keyword, options),
                    },
                    {
                        state: {
                            queryRawResults: result,
                            initialFilterState: initialFilterState,
                            searchInput: keyword,
                            leftOverParam: keyword,
                        },
                    }
                );
                return; // 完成後直接結束，避免落到下面邏輯
            } else if (tree.length > 1) {
                console.log(tree.length > 1);
                navigate(`/browse/products?search=${encodeURIComponent(keyword)}`, {
                    state: {
                        queryRawResults: result,
                        searchInput: keyword,
                        customBannerMessage:
                            'We have identified several product categories that match your search.',
                        // pimCoreFilteredItems.length >= 1 means needs to include resource tab
                        includeResourceLibrary: newFiltered.length >= 1,
                        excludeProducts: false,
                    },
                });
                return;
            }

            // 如果 result的 detectDefinitons 的長度大於1 倒轉到 BrowseAllProductPage
            if (result.detectedDefinitions.length > 1) {
                console.log('result.detectedDefinitions.length > 1');
                // 導向到 BrowseAllProductPage
                navigate(`/browse/products?search=${encodeURIComponent(keyword)}`, {
                    state: {
                        queryRawResults: result,
                        searchInput: keyword,
                        customBannerMessage:
                            'We have identified several product categories that match your search.',
                        // pimCoreFilteredItems.length >= 1 means needs to include resource tab
                        includeResourceLibrary: newFiltered.length >= 1,
                        excludeProducts: false,
                    },
                });
                return;

                // 如果 result的 detectDefinitons 的長度===1 倒轉到 ProductHome
            } else if (result.detectedDefinitions.length === 1) {
                console.log('result.detectedDefinitions.length === 1');

                const definition = result.detectedDefinitions[0];
                const type = definition?.childDefinitions.map((d) => d.id);

                // 構建 initialFilterState
                const initialFilterState: DynamicRequestBody = {
                    '1': definition?.id ? definition?.id : 1, // 基本的 filter state
                    ...parameterValues, // 添加處理後的 parameter values
                };

                // 如果 type 不是空陣列，加入 79 key
                if (type?.length) {
                    initialFilterState['79'] = type;
                }

                // console.log('initialFilterState :', initialFilterState);

                // 導向到 ProductHome
                navigate(
                    {
                        pathname: `/products/${definition?.name}`,
                        search: buildSearchQuery(keyword, options),
                    },
                    {
                        state: {
                            queryRawResults: result,
                            initialFilterState: initialFilterState,
                            searchInput: keyword,
                            leftOverParam: keyword,
                        },
                    }
                );
            }

            // new request
            if (result.message && result.message.type === 'ERROR') {
                console.warn('result.message && result.message.type === ERROR');
                console.log('newFiltered=', newFiltered);
                if (newFiltered.length >= 1) {
                    navigate(`/browse/products?search=${encodeURIComponent(keyword)}`, {
                        state: {
                            queryRawResults: result,
                            searchInput: keyword,
                            leftOverParam: keyword,
                            customBannerMessage:
                                'We have identified several results that match your search.',
                            includeResourceLibrary: true,
                            excludeProducts: true,
                        },
                    });
                } else {
                    console.log('我是error');
                    navigate(`/browse/products?search=${encodeURIComponent(keyword)}`, {
                        state: {
                            queryRawResults: result,
                            searchInput: keyword,
                            leftOverParam: keyword,
                            customBannerMessage:
                                'We are unable to find any products that match your search terms. Please browse our products.',
                            includeResourceLibrary: false,
                            excludeProducts: false,
                        },
                    });
                }
            }
        } catch (err: any) {
            console.error('err', err);
            navigate(`/browse/products?search=${encodeURIComponent(keyword)}`, {
                state: {
                    queryRawResults: undefined,
                    searchInput: keyword,
                    leftOverParam: keyword,
                    customBannerMessage:
                        'We are unable to find any products that match your search terms. Please browse our products.',
                    includeResourceLibrary: false,
                    excludeProducts: false,
                },
            });
        } finally {
            // 完成後關閉 Loading
            props?.setIsLoading?.(false);
        }
    };

    return {
        searchByKeyword,
    };
}
